// # Terms of Service Page Component
import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import Header from '../components/header';  // # Import regular header
import DashboardFooter from '../../components/DashboardFooter';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';
import './legal.css';

function Terms() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user, isAuthenticated } = useAuthStore();  // # Get user and auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Add hero background style
  const heroStyle = {
    background: `linear-gradient(rgba(44, 62, 80, 0.7), rgba(44, 62, 80, 0.7)), url(${process.env.PUBLIC_URL + '/icons/terms.jpg'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <div className={`legal_terms-page ${isDarkMode ? 'legal_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}
      
      <main className="legal_terms-content">
        <section className="legal_hero-section" style={heroStyle}>
          <h1>Terms of Service</h1>
          <p>Please read these terms carefully before using TEMP-U services</p>
        </section>

        <section className="legal_terms-section">
          <div className="legal_terms-container">
            <div className="legal_terms-group">
              <h2>1. Subscription and Services</h2>
              <div className="legal_terms-item">
                <h3>1.1 Service Description</h3>
                <p>TEMP-U provides USMLE preparation services including but not limited to online study materials, mentorship programs, and educational resources.</p>
              </div>
              <div className="legal_terms-item">
                <h3>1.2 Subscription Terms</h3>
                <div>
                  <h4>Mentorship Subscriptions</h4>
                  <p>Mentorship Subscriptions are billed on a monthly basis. Users may cancel their subscription at any time, effective at the end of the current billing period.</p>
                </div>
                <div>
                  <h4>Library Subscriptions</h4>
                  <p>Library Subscriptions are one-time billing that cannot be cancelled. But we are 100% sure you will not need to cancel. The Library is without a doubt the best resource to study for the USMLE.</p>
                </div>
                <div>
                  <h4>Delayed Subscription Start</h4>
                  <p>Can I pay for my subscription now, and start the subscription later?</p>
                  <p>Yes, definitely you can! But you will have a maximum period of 4 months to start the subscriptions, after which, the subscription will start counting.</p>
                </div>
              </div>
              
              <div className="legal_terms-item">
                <h3>1.3 Refund Policy</h3>
                <div>
                  <h4>Mentorship Enrollment Fee</h4>
                  <p>The enrollment fee for the mentorship program is non-refundable under any circumstances as it covers administrative costs, onboarding, and mentor preparation.</p>
                </div>
                <div>
                  <h4>Monthly Mentorship Fee</h4>
                  <ul className="legal_terms-list">
                    <li>First Monthly Payment:
                      <ul>
                        <li>Within 7 days of first session: Full refund available upon request</li>
                        <li>After 7 days: Non-refundable</li>
                      </ul>
                    </li>
                    <li>Subsequent Monthly Payments: All fees starting from the second monthly payment are non-refundable</li>
                  </ul>
                </div>
                <div>
                  <h4>Library Access</h4>
                  <p className="legal_terms-highlight"><strong>Library purchases are non-refundable under any circumstances.</strong></p>
                </div>
              </div>

              <div className="legal_terms-item">
                <h3>1.4 Subscription Freeze Policy</h3>
                <div>
                  <h4>Eligibility and Duration</h4>
                  <p>During your USMLE preparation journey, subscribers are entitled to temporarily suspend their mentorship subscription under the following conditions:</p>
                  <ul className="legal_terms-list">
                    <li>A maximum of two (2) separate freeze periods are permitted throughout your entire subscription duration</li>
                    <li>The cumulative freeze duration shall not exceed four (4) weeks in total</li>
                  </ul>
                </div>
                <div>
                  <h4>Terms and Conditions</h4>
                  <ul className="legal_terms-list">
                    <li>Freeze requests must be submitted at least 48 hours before the intended pause period</li>
                    <li>During the freeze period, access to mentorship services will be temporarily suspended</li>
                    <li>Your subscription will automatically be extended by the duration of the freeze period</li>
                  </ul>
                  <p className="legal_terms-highlight"><strong>Note: The freeze policy applies exclusively to mentorship subscriptions and does not affect library access or other services.</strong></p>
                </div>
              </div>
            </div>

            <div className="legal_terms-group">
              <h2>2. User Responsibilities</h2>
              <div className="legal_terms-item">
                <h3>2.1 Account Security</h3>
                <p>Users are responsible for maintaining the confidentiality of their account credentials and for all activities under their account.</p>
                <p className="legal_terms-highlight"><strong>Kindely note that you are only allowed to be signed in on 3 devices at a time, this helps us maintain our security rules and fair use policy.</strong></p>
              </div>
              <div className="legal_terms-item">
                <h3>2.2 Prohibited Activities</h3>
                <p>Users may not share their account access, redistribute materials, or engage in any activity that violates these terms.</p>
              </div>
            </div>

            <div className="legal_terms-group">
              <h2>3. Content Usage</h2>
              <div className="legal_terms-item">
                <h3>3.1 Intellectual Property</h3>
                <p>All content provided through TEMP-U services is protected by copyright and other intellectual property laws.</p>
              </div>
              <div className="legal_terms-item">
                <h3>3.2 Usage Restrictions</h3>
                <p>Materials are for personal use only and may not be shared, copied, or distributed without explicit permission.</p>
              </div>
            </div>

            <div className="legal_terms-group">
              <h2>4. Termination</h2>
              <div className="legal_terms-item">
                <p className="legal_terms-highlight"><strong>TEMP-U reserves the right to terminate or suspend access to services for violations of these terms or any other reason deemed necessary.</strong></p>
                <p className="legal_terms-highlight"><strong>Suspicious patterns of activity will be investigated and may result in account termination, a temporary or even, a permanent ban.</strong></p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Terms; 